import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { tasksAPI, usersAPI } from '../../services/api';
import {
  tasksExtendedAPI,
  componentsAPI,
  versionsAPI,
} from '../../services/taskServices';
import { Task, TASK_PRIORITIES, TASK_ITEM_TYPES } from '../../types/task';
import TaskComments from './TaskComments';
import TaskLinks from './TaskLinks';
import TaskAttachments from './TaskAttachments';
import {
  ArrowBackIcon,
  ChevronDownPathIcon,
  EyeIcon,
  FlagIcon,
  XIcon,
  CalendarIcon,
  ClockIcon,
  UserIcon,
  FolderIcon,
} from '../icons';

interface User {
  id: number;
  full_name: string;
  email: string;
}

// Icons
const Icons = {
  back: <ArrowBackIcon className="w-5 h-5" strokeWidth={2} />,
  chevronDown: <ChevronDownPathIcon className="w-4 h-4" strokeWidth={2} />,
  eye: <EyeIcon className="w-4 h-4" strokeWidth={2} />,
  flag: <FlagIcon className="w-4 h-4" strokeWidth={2} />,
  close: <XIcon className="w-4 h-4" strokeWidth={2} />,
  calendar: <CalendarIcon className="w-4 h-4" strokeWidth={2} />,
  clock: <ClockIcon className="w-4 h-4" strokeWidth={2} />,
  user: <UserIcon className="w-4 h-4" strokeWidth={2} />,
  folder: <FolderIcon className="w-4 h-4" strokeWidth={2} />,
};

const TaskDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [task, setTask] = useState<Task | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<
    'comments' | 'links' | 'attachments'
  >('comments');

  // Editing states
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<any>('');
  const [saving, setSaving] = useState(false);

  // Dropdown states
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [assigneeDropdownOpen, setAssigneeDropdownOpen] = useState(false);

  // Theme classes
  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 backdrop-blur-sm rounded-2xl'
    : 'bg-white border border-slate-200 shadow-sm rounded-2xl';

  const inputClass = isDark
    ? 'w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 text-white placeholder-slate-500 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors'
    : 'w-full px-4 py-2.5 bg-white border border-slate-200 text-slate-900 placeholder-slate-400 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors';

  const fetchTask = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await tasksAPI.getById(Number(id));
      setTask(response.data.task);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || t('tasks.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  useEffect(() => {
    fetchTask();
    fetchUsers();
  }, [fetchTask]);

  const updateTask = async (field: string, value: any) => {
    if (!task) return;
    setSaving(true);
    try {
      const response = await tasksAPI.update(task.id, { [field]: value });
      console.log('Update response:', response);
      await fetchTask();
      setEditingField(null);
    } catch (err: any) {
      console.error('Failed to update task:', err);
      const errorMsg =
        err.response?.data?.error || err.message || 'Failed to update task';
      alert(`Error: ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleFlag = async () => {
    if (!task) return;
    try {
      if (task.flagged) {
        await tasksExtendedAPI.unflag(task.id);
      } else {
        await tasksExtendedAPI.flag(task.id);
      }
      fetchTask();
    } catch (err) {
      console.error('Failed to toggle flag:', err);
    }
  };

  const startEditing = (field: string, currentValue: any) => {
    setEditingField(field);
    setEditValue(currentValue || '');
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditValue('');
  };

  const saveField = async (field: string) => {
    await updateTask(field, editValue);
  };

  const getStatusColor = (status: string) => {
    if (isDark) {
      switch (status) {
        case 'todo':
          return 'bg-slate-500/20 text-slate-400 border border-slate-500/30';
        case 'in_progress':
          return 'bg-amber-500/20 text-amber-400 border border-amber-500/30';
        case 'completed':
          return 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30';
        case 'blocked':
          return 'bg-rose-500/20 text-rose-400 border border-rose-500/30';
        default:
          return 'bg-slate-500/20 text-slate-400 border border-slate-500/30';
      }
    } else {
      switch (status) {
        case 'todo':
          return 'bg-slate-100 text-slate-700 border border-slate-200';
        case 'in_progress':
          return 'bg-amber-50 text-amber-700 border border-amber-200';
        case 'completed':
          return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
        case 'blocked':
          return 'bg-rose-50 text-rose-700 border border-rose-200';
        default:
          return 'bg-slate-100 text-slate-700 border border-slate-200';
      }
    }
  };

  const getPriorityColor = (priority: string) => {
    if (isDark) {
      switch (priority) {
        case 'low':
        case 'lowest':
          return 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30';
        case 'medium':
          return 'bg-amber-500/20 text-amber-400 border border-amber-500/30';
        case 'high':
        case 'highest':
          return 'bg-orange-500/20 text-orange-400 border border-orange-500/30';
        case 'critical':
          return 'bg-rose-500/20 text-rose-400 border border-rose-500/30';
        default:
          return 'bg-slate-500/20 text-slate-400 border border-slate-500/30';
      }
    } else {
      switch (priority) {
        case 'low':
        case 'lowest':
          return 'bg-cyan-50 text-cyan-700 border border-cyan-200';
        case 'medium':
          return 'bg-amber-50 text-amber-700 border border-amber-200';
        case 'high':
        case 'highest':
          return 'bg-orange-50 text-orange-700 border border-orange-200';
        case 'critical':
          return 'bg-rose-50 text-rose-700 border border-rose-200';
        default:
          return 'bg-slate-100 text-slate-700 border border-slate-200';
      }
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'todo':
        return t('tasks.status.todo');
      case 'in_progress':
        return t('tasks.status.inProgress');
      case 'completed':
        return t('tasks.status.completed');
      case 'blocked':
        return t('tasks.status.blocked');
      default:
        return status;
    }
  };

  const getPriorityInfo = (priority: string) => {
    return (
      TASK_PRIORITIES.find((p) => p.value === priority) || TASK_PRIORITIES[2]
    );
  };

  const getItemTypeInfo = (itemType: string) => {
    return (
      TASK_ITEM_TYPES.find((t) => t.value === itemType) || TASK_ITEM_TYPES[0]
    );
  };

  const formatDate = (date: string) => new Date(date).toLocaleDateString();
  const formatDateTime = (date: string) => new Date(date).toLocaleString();

  if (loading) {
    return (
      <div
        className={`min-h-screen p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
      >
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div
        className={`min-h-screen p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
      >
        <div className={`${cardClass} p-6`}>
          <div className="text-rose-500 mb-4">
            {error || t('tasks.notFound')}
          </div>
          <button
            onClick={() => navigate(-1)}
            className={`inline-flex items-center gap-2 text-sm ${isDark ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-700'}`}
          >
            {Icons.back}
            {t('common.goBack')}
          </button>
        </div>
      </div>
    );
  }

  const priorityInfo = task.priority
    ? getPriorityInfo(task.priority)
    : { label: 'None', color: 'text-slate-500' };
  const itemTypeInfo = getItemTypeInfo(task.item_type);

  return (
    <div className={`min-h-screen ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}>
      {/* Header */}
      <div
        className={`sticky top-0 z-20 ${isDark ? 'bg-slate-900/95 border-slate-700/50' : 'bg-slate-50/95 border-slate-200'} border-b backdrop-blur-sm`}
      >
        <div className="px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => navigate(-1)}
                className={`p-2 rounded-xl transition-colors ${isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
              >
                {Icons.back}
              </button>
              <div className="flex items-center gap-2 text-sm min-w-0">
                <Link
                  to="/manager/projects"
                  className={`hidden sm:inline truncate ${isDark ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-700'}`}
                >
                  {task.project?.name}
                </Link>
                <span
                  className={`hidden sm:inline ${isDark ? 'text-slate-600' : 'text-slate-400'}`}
                >
                  /
                </span>
                <span
                  className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}
                >
                  TASK-{task.id}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <button
                className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm ${isDark ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-500 hover:bg-slate-100'}`}
              >
                {Icons.eye}
                <span className="hidden sm:inline">
                  {task.watcher_count || 0}
                </span>
              </button>
              <button
                onClick={handleFlag}
                className={`p-2 rounded-lg transition-colors ${task.flagged ? 'bg-orange-500/20 text-orange-400' : isDark ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-500 hover:bg-slate-100'}`}
              >
                {Icons.flag}
              </button>
              <button
                onClick={() => navigate(-1)}
                className={`p-2 rounded-lg transition-colors ${isDark ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-500 hover:bg-slate-100'}`}
              >
                {Icons.close}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 sm:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_380px] gap-6">
          {/* Main Content */}
          <div className="flex-1  space-y-6">
            {/* Title & Status */}
            <div className={cardClass}>
              <div className="p-4 sm:p-6">
                {/* Status Dropdown */}
                <div className="relative mb-4">
                  <button
                    onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium ${getStatusColor(task.status)}`}
                  >
                    {getStatusLabel(task.status)}
                    {Icons.chevronDown}
                  </button>
                  {statusDropdownOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setStatusDropdownOpen(false)}
                      />
                      <div
                        className={`absolute top-full left-0 mt-2 z-20 min-w-[160px] rounded-xl shadow-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}
                      >
                        {['todo', 'in_progress', 'completed', 'blocked'].map(
                          (status) => (
                            <button
                              key={status}
                              onClick={() => {
                                updateTask('status', status);
                                setStatusDropdownOpen(false);
                              }}
                              className={`w-full px-4 py-2.5 text-left text-sm transition-colors first:rounded-t-xl last:rounded-b-xl ${
                                task.status === status
                                  ? isDark
                                    ? 'bg-violet-500/20 text-violet-400'
                                    : 'bg-violet-50 text-violet-700'
                                  : isDark
                                    ? 'text-slate-300 hover:bg-slate-700'
                                    : 'text-slate-700 hover:bg-slate-50'
                              }`}
                            >
                              {getStatusLabel(status)}
                            </button>
                          ),
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Title */}
                {editingField === 'title' ? (
                  <div className="mb-4">
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className={inputClass}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveField('title');
                        if (e.key === 'Escape') cancelEditing();
                      }}
                    />
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => saveField('title')}
                        disabled={saving}
                        className="px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white text-sm font-medium rounded-xl"
                      >
                        {saving ? '...' : t('common.save')}
                      </button>
                      <button
                        onClick={cancelEditing}
                        className={`px-4 py-2 text-sm font-medium rounded-xl ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-700'}`}
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <h1
                    onClick={() => startEditing('title', task.title)}
                    className={`text-xl sm:text-2xl font-bold cursor-pointer hover:opacity-80 transition-opacity ${isDark ? 'text-white' : 'text-slate-900'}`}
                  >
                    {task.title}
                  </h1>
                )}

                {/* Badges */}
                <div className="flex flex-wrap gap-2 mt-4">
                  <span
                    className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-lg ${task.priority ? getPriorityColor(task.priority) : 'bg-slate-200 text-slate-700'}`}
                  >
                    {priorityInfo.label}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-700'}`}
                  >
                    {itemTypeInfo.label}
                  </span>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className={cardClass}>
              <div className="p-4 sm:p-6">
                <h2
                  className={`text-sm font-semibold uppercase tracking-wide mb-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                >
                  {t('tasks.form.description')}
                </h2>
                {editingField === 'description' ? (
                  <div>
                    <textarea
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className={`${inputClass} min-h-[120px]`}
                      autoFocus
                    />
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => saveField('description')}
                        disabled={saving}
                        className="px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white text-sm font-medium rounded-xl"
                      >
                        {saving ? '...' : t('common.save')}
                      </button>
                      <button
                        onClick={cancelEditing}
                        className={`px-4 py-2 text-sm font-medium rounded-xl ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-700'}`}
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() =>
                      startEditing('description', task.description)
                    }
                    className={`text-sm whitespace-pre-wrap cursor-pointer min-h-[60px] ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                  >
                    {task.description || (
                      <span
                        className={
                          isDark
                            ? 'text-slate-500 italic'
                            : 'text-slate-400 italic'
                        }
                      >
                        {t('tasks.form.description')}...
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Attachments */}
            <div className={cardClass}>
              <div className="p-4 sm:p-6">
                <h2
                  className={`text-sm font-semibold uppercase tracking-wide mb-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                >
                  {t('tasks.attachments')}
                  {(task.attachment_count || 0) > 0 && (
                    <span
                      className={`ml-2 px-2 py-0.5 text-xs rounded-full ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}
                    >
                      {task.attachment_count}
                    </span>
                  )}
                </h2>
                <TaskAttachments taskId={task.id} />
              </div>
            </div>

            {/* Activity */}
            <div className={cardClass}>
              <div className="p-4 sm:p-6">
                <h2
                  className={`text-sm font-semibold uppercase tracking-wide mb-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                >
                  {t('tasks.activity')}
                </h2>
                <div className="flex gap-2 mb-4">
                  {[
                    { key: 'comments', label: t('tasks.comments') },
                    { key: 'links', label: t('tasks.linkedItems') },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key as any)}
                      className={`px-4 py-2 text-sm font-medium rounded-xl transition-colors ${
                        activeTab === tab.key
                          ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white'
                          : isDark
                            ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                {activeTab === 'comments' && <TaskComments taskId={task.id} />}
                {activeTab === 'links' && (
                  <TaskLinks taskId={task.id} onUpdate={fetchTask} />
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="w-full  flex-shrink-0">
            <div className={`${cardClass} sticky top-24`}>
              <div
                className={`px-4 py-3 border-b ${isDark ? 'border-slate-700/50' : 'border-slate-100'}`}
              >
                <span
                  className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
                >
                  {t('tasks.details')}
                </span>
              </div>

              <div className="p-4 space-y-4">
                {/* Assignee */}
                <div className="flex items-center justify-between">
                  <span
                    className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                  >
                    {t('tasks.assignee')}
                  </span>
                  <div className="relative">
                    <button
                      onClick={() =>
                        setAssigneeDropdownOpen(!assigneeDropdownOpen)
                      }
                      className={`flex items-center gap-2 px-2 py-1 rounded-lg ${isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}
                    >
                      {task.assigned_to ? (
                        <>
                          <div className="w-6 h-6 bg-gradient-to-br from-violet-500 to-purple-600 text-white rounded-full flex items-center justify-center text-xs font-medium">
                            {task.assigned_to.initials ||
                              task.assigned_to.name?.charAt(0)}
                          </div>
                          <span
                            className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                          >
                            {task.assigned_to.name}
                          </span>
                        </>
                      ) : (
                        <span
                          className={`text-sm ${isDark ? 'text-violet-400' : 'text-violet-600'}`}
                        >
                          {t('tasks.unassigned')}
                        </span>
                      )}
                    </button>
                    {assigneeDropdownOpen && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setAssigneeDropdownOpen(false)}
                        />
                        <div
                          className={`absolute top-full right-0 mt-2 z-20 min-w-[200px] max-h-[300px] overflow-y-auto rounded-xl shadow-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}
                        >
                          <button
                            onClick={() => {
                              updateTask('assigned_to_user_id', null);
                              setAssigneeDropdownOpen(false);
                            }}
                            className={`w-full px-4 py-2.5 text-left text-sm ${isDark ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-500 hover:bg-slate-50'}`}
                          >
                            {t('tasks.unassigned')}
                          </button>
                          {users.map((user) => (
                            <button
                              key={user.id}
                              onClick={() => {
                                updateTask('assigned_to_user_id', user.id);
                                setAssigneeDropdownOpen(false);
                              }}
                              className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 ${isDark ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50'}`}
                            >
                              <div className="w-6 h-6 bg-gradient-to-br from-violet-500 to-purple-600 text-white rounded-full flex items-center justify-center text-xs">
                                {user.full_name?.charAt(0) ||
                                  user.email?.charAt(0)}
                              </div>
                              {user.full_name || user.email}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Project */}
                <div className="flex items-center justify-between">
                  <span
                    className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                  >
                    {t('tasks.form.project')}
                  </span>
                  <span
                    className={`text-sm flex items-center gap-1.5 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                  >
                    {Icons.folder}
                    {task.project?.name}
                  </span>
                </div>

                {/* Dates */}
                <div className="flex items-center justify-between">
                  <span
                    className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                  >
                    {t('tasks.startDate')}
                  </span>
                  <span
                    className={`text-sm flex items-center gap-1.5 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                  >
                    {Icons.calendar}
                    {task.start_date ? formatDate(task.start_date) : '-'}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span
                    className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                  >
                    {t('tasks.columns.dueDate')}
                  </span>
                  <span
                    className={`text-sm flex items-center gap-1.5 ${
                      task.due_date &&
                      new Date(task.due_date) < new Date() &&
                      task.status !== 'completed'
                        ? 'text-rose-500'
                        : isDark
                          ? 'text-slate-300'
                          : 'text-slate-700'
                    }`}
                  >
                    {Icons.calendar}
                    {task.due_date ? formatDate(task.due_date) : '-'}
                  </span>
                </div>

                {/* Time */}
                <div className="flex items-center justify-between">
                  <span
                    className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                  >
                    {t('tasks.timeTracking')}
                  </span>
                  <span
                    className={`text-sm flex items-center gap-1.5 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                  >
                    {Icons.clock}
                    {task.time_spent ? `${task.time_spent}h` : '0h'}
                  </span>
                </div>

                {/* Story Points */}
                {task.story_points !== undefined &&
                  task.story_points !== null && (
                    <div className="flex items-center justify-between">
                      <span
                        className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {t('kanban.storyPoints')}
                      </span>
                      <span
                        className={`text-sm font-medium ${isDark ? 'text-violet-400' : 'text-violet-600'}`}
                      >
                        {task.story_points}
                      </span>
                    </div>
                  )}

                {/* Reporter */}
                {task.reporter && (
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                    >
                      {t('tasks.reporter')}
                    </span>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-gradient-to-br from-emerald-500 to-green-600 text-white rounded-full flex items-center justify-center text-xs font-medium">
                        {task.reporter.initials ||
                          task.reporter.name?.charAt(0)}
                      </div>
                      <span
                        className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                      >
                        {task.reporter.name}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Timestamps */}
              <div
                className={`px-4 py-3 border-t ${isDark ? 'border-slate-700/50' : 'border-slate-100'}`}
              >
                <div
                  className={`text-xs space-y-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                >
                  <div>
                    {t('common.created')}: {formatDateTime(task.created_at)}
                  </div>
                  <div>
                    {t('common.updated')}: {formatDateTime(task.updated_at)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskDetailPage;
